import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { changePassword } from 'src/services/Login/ForgotPassword';
import Tostify, { showToast } from 'src/utils/Tostify';
import LogoHeader from 'src/components/header/LogoHeader';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

// Constants
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
const ENCRYPTION_KEY = process.env.REACT_APP_KEY; // Ensure this is securely managed

// Utility Function
const generateAESHash = (data) => CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();

function ConfirmNewPassword() {
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const location = useLocation();
  const userId = location.state?.userId[0];
  const navigate = useNavigate();

  const toggleShowPassword = (field) =>
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));

  const validateForm = (updatedFormData) => {
    const newErrors = {
      newPassword: !updatedFormData.newPassword
        ? 'New password is required'
        : !PASSWORD_PATTERN.test(updatedFormData.newPassword)
        ? 'Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character'
        : '',
      confirmPassword: !updatedFormData.confirmPassword
        ? 'Confirm password is required'
        : updatedFormData.newPassword !== updatedFormData.confirmPassword
        ? 'Passwords do not match'
        : '',
    };
    if (userId && updatedFormData.newPassword.includes(userId)) {
      newErrors.newPassword = 'Password should not contain your user ID';
    }
    return newErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    setErrors(validateForm(updatedFormData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      showToast('Invalid user ID. Please try again.', 'error');
      return;
    }
    const newErrors = validateForm(formData);
    setErrors(newErrors);
    if (Object.values(newErrors).some((error) => error)) return;

    const encryptedData = {
      userId,
      newPassword: generateAESHash(formData.newPassword),
      confirmPassword: generateAESHash(formData.confirmPassword),
    };
    setLoading(true);
    try {
      const response = await changePassword(encryptedData);
      setLoading(false);
      if (response.success) {
        localStorage.removeItem('token');
        showToast('Password changed successfully', 'success');
        setFormData({ newPassword: '', confirmPassword: '' });
        setErrors({ newPassword: '', confirmPassword: '' });
        setTimeout(() => navigate('/login'), 2000);
      } else {
        showToast(response.message, 'error');
      }
    } catch (error) {
      setLoading(false);
      showToast('An error occurred. Please try again later.', 'error');
    }
  };

  return (
    <div className="card">
      <LogoHeader />
      <div className="row justify-content-center" style={{ marginTop: '50px' }}>
        <div className="w-50">
          <div className="card mx-4">
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-12">
                    <strong style={{ marginLeft: '10px' }}>Change Password</strong>
                  </div>
                </div>
                <hr />
                {['newPassword', 'confirmPassword'].map((field) => (
                  <div className="input-group mb-3" key={field}>
                    <span className="input-group-text">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        className="icon"
                        role="img"
                        aria-hidden="true"
                      >
                        <path
                          fill="var(--ci-primary-color, currentColor)"
                          d="M384,200V144a128,128,0,0,0-256,0v56H88V328c0,92.635,75.364,168,168,168s168-75.365,168-168V200ZM160,144a96,96,0,0,1,192,0v56H160ZM392,328c0,74.99-61.01,136-136,136s-136-61.01-136-136V232H392Z"
                          className="ci-primary"
                        />
                      </svg>
                    </span>
                    <input
                      type={showPasswords[field] ? 'text' : 'password'}
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
                      placeholder={field === 'newPassword' ? 'New Password' : 'Confirm Password'}
                      autoComplete="new-password"
                      required
                      pattern={field === 'newPassword' ? PASSWORD_PATTERN.source : undefined}
                    />
                    <span
                      className="input-group-text"
                      onClick={() => toggleShowPassword(field)}
                      style={{ cursor: 'pointer' }}
                      aria-label={`Toggle ${field} visibility`}
                    >
                      {showPasswords[field] ? <FaEyeSlash /> : <FaEye />}
                    </span>
                    {errors[field] && <div className="invalid-feedback">{errors[field]}</div>}
                  </div>
                ))}
                <div className="text-center">
                  <button
                    className="btn"
                    style={{ backgroundColor: '#3c4b64', color: 'white' }}
                    type="submit"
                    disabled={loading || !userId || Object.values(errors).some((error) => error)}
                  >
                    {loading ? 'Changing Password...' : 'Change Password'}
                  </button>
                </div>
              </form>
              <ul style={{ listStyle: 'square', color: 'red', fontSize: '14px' }}>
                <strong>Password requirements:</strong>
                <li>At least 8 characters</li>
                <li>At least one uppercase letter</li>
                <li>At least one lowercase letter</li>
                <li>At least one number</li>
                <li>No white spaces</li>
                <li>At least one special character (@, #, $, ?, etc.)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <Tostify />
    </div>
  );
}

export default ConfirmNewPassword;